import React, { useEffect, useState } from "react";
import {
    getBusinessById,
    getEmployeeByBusiness,
    getTipsByBusiness,
} from "../../services/firestoreService";
import { format, subDays } from "date-fns";
import {
    ResponsiveContainer,
    AreaChart,
    Area,
    LineChart,
    Line,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    BarChart,
    Bar,
    Legend,
} from "recharts";

import BusinessLayoutV2 from "../../components/BusinessLayoutV2";
import {useAuth} from "../../context/AuthProvider";

const FILTER_OPTIONS = {
    "Last 24 hours": 1,
    "All time": null,
    "Last 7 days": 7,
    "Last 14 days": 14,
    "Last 30 days": 30,
    "Last 90 days": 90,
    "Last 180 days": 180,
    "Last year": 365,
};

const CustomTooltip = ({ active, payload, label }) => {
    if (!active || !payload?.length) return null;
    return (
        <div
            style={{
                background: "#fff",
                border: "1px solid #e8e8ee",
                borderRadius: 12,
                padding: "10px 16px",
                boxShadow: "0 8px 24px rgba(0,0,0,0.08)",
            }}
        >
            <p style={{ color: "#8b8b9e", fontSize: 12, margin: 0 }}>{label}</p>
            <p style={{ color: "#0d7c5f", fontSize: 18, fontWeight: 700, margin: "2px 0 0" }}>
                €{Number(payload[0].value || 0).toFixed(2)}
            </p>
        </div>
    );
};

function BusinessStatisticsV2() {
    const {userData} = useAuth();

    const [employees, setEmployees] = useState([]);
    const [tips, setTips] = useState([]);
    const [filterDays, setFilterDays] = useState(30);
    const [business, setBusiness] = useState(null);

    const visibleEmployees = (employeeData) =>
        (Array.isArray(employeeData) ? employeeData : []).filter(
            (employee) => String(employee.accountStatus || "").toLowerCase() !== "deactivated"
        );

    useEffect(() => {
        const fetchData = async () => {
            const businessData = await getBusinessById(userData.businessID);
            const employeeData = await getEmployeeByBusiness(userData.businessID);
            const tipsData = await getTipsByBusiness(userData.businessID);

            const teamEnabled =
                businessData?.config?.teamEnabled ?? false;
            const teamEmployee = teamEnabled ? [{id: "team", displayName: "Team"}] : [];
            const activeEmployees = visibleEmployees(employeeData);
            const activeEmployeeIDs = new Set(activeEmployees.map((employee) => employee.id));
            const visibleTips = (Array.isArray(tipsData) ? tipsData : []).filter(
                (tip) => (teamEnabled && tip.employeeID === "team") || activeEmployeeIDs.has(tip.employeeID)
            );

            setBusiness(businessData);
            // setEmployees(employeeData);
            setEmployees([...activeEmployees, ...teamEmployee]);
            // setEmployees(employeeData);
            setTips(visibleTips);
        };
        fetchData();
    }, [userData]);

    const filteredTips = tips.filter((tip) => {
        if (!filterDays) return true;
        const tipDate = new Date(tip.createdAt.seconds * 1000);
        return tipDate >= subDays(new Date(), filterDays);
    });

    const today = new Date();
    let tipChartData;

    if (filterDays === 1) {
        const hourlyMap = {};
        for (let i = 0; i < 24; i++) {
            const label = `${String(i).padStart(2, '0')}:00`;
            hourlyMap[label] = 0;
        }

        filteredTips.forEach((tip) => {
            const date = new Date(tip.createdAt.seconds * 1000);
            const label = `${String(date.getHours()).padStart(2, '0')}:00`;
            if (hourlyMap[label] !== undefined) {
                hourlyMap[label] += tip.amount;
            }
        });

        tipChartData = Object.entries(hourlyMap).map(([date, amount]) => ({
            date,
            amount: Number(amount.toFixed(2)),
        }));
    } else {
        const dateMap = {};
        for (let i = (filterDays || 30) - 1; i >= 0; i--) {
            const date = subDays(today, i);
            const label = format(date, "MMM d");
            dateMap[label] = 0;
        }

        filteredTips.forEach((tip) => {
            const date = format(new Date(tip.createdAt.seconds * 1000), "MMM d");
            if (dateMap[date] !== undefined) {
                dateMap[date] += tip.amount;
            }
        });

        tipChartData = Object.entries(dateMap).map(([date, amount]) => ({
            date,
            amount: Number(amount.toFixed(2)),
        }));
    }

    const tipDaysSet = new Set(filteredTips.map((t) => new Date(t.createdAt.seconds * 1000).toDateString()));
    const avgTipsPerDay = tipDaysSet.size ? (filteredTips.reduce((a, b) => a + b.amount, 0) / tipDaysSet.size).toFixed(2) : "0.00";

    const monthlyTotal = filteredTips.reduce((sum, t) => sum + t.amount, 0).toFixed(2);
    const totalTips = filteredTips.reduce((sum, t) => sum + t.amount, 0).toFixed(2);

    const employeeStats = employees.map((e) => {
        const empTips = filteredTips.filter((t) => t.employeeID === e.id);
        const empDays = new Set(empTips.map((t) => new Date(t.createdAt.seconds * 1000).toDateString()));
        const dailyAvg = empDays.size ? empTips.reduce((s, t) => s + t.amount, 0) / empDays.size : 0;
        return { id: e.id, displayName: e.displayName, avg: dailyAvg };
    }).sort((a, b) => b.avg - a.avg);

    const topEmployee = employeeStats[0] || { name: "N/A", avg: 0 };

    const weekdayMap = { Sun: 0, Mon: 0, Tue: 0, Wed: 0, Thu: 0, Fri: 0, Sat: 0 };
    filteredTips.forEach((tip) => {
        const day = format(new Date(tip.createdAt.seconds * 1000), "eee");
        weekdayMap[day] += tip.amount;
    });

    const tipsByWeekday = Object.keys(weekdayMap).map((day) => ({
        day,
        amount: Number(weekdayMap[day].toFixed(2)),
    }));

    const allDates = [];
    for (let i = (filterDays || 30) - 1; i >= 0; i--) {
        allDates.push(format(subDays(today, i), "MMM d"));
    }

    let employeeTrends;

    if (filterDays === 1) {
        const hours = Array.from({ length: 24 }, (_, i) => `${String(i).padStart(2, '0')}:00`);
        employeeTrends = hours.map((hourLabel) => {
            const entry = { date: hourLabel };
            employees.forEach((emp) => {
                const empTipsForHour = filteredTips.filter((t) => {
                    const date = new Date(t.createdAt.seconds * 1000);
                    return (
                        t.employeeID === emp.id &&
                        `${String(date.getHours()).padStart(2, '0')}:00` === hourLabel
                    );
                });
                entry[emp.displayName] = empTipsForHour.reduce((sum, t) => sum + t.amount, 0);
            });
            return entry;
        });
    } else {
        const allDates = [];
        for (let i = (filterDays || 30) - 1; i >= 0; i--) {
            allDates.push(format(subDays(today, i), "MMM d"));
        }

        employeeTrends = allDates.map((date) => {
            const entry = { date };
            employees.forEach((emp) => {
                const empTipsForDate = filteredTips.filter((t) => {
                    return (
                        t.employeeID === emp.id &&
                        format(new Date(t.createdAt.seconds * 1000), "MMM d") === date
                    );
                });
                entry[emp.displayName] = empTipsForDate.reduce((sum, t) => sum + t.amount, 0);
            });
            return entry;
        });
    }


    const employeeDetailStats = employees
        .map((emp) => {
            const empTips = filteredTips.filter((t) => t.employeeID === emp.id);
            const empDays = new Set(empTips.map((t) => new Date(t.createdAt.seconds * 1000).toDateString()));
            const dailyAvg = empDays.size ? empTips.reduce((s, t) => s + t.amount, 0) / empDays.size : 0;
            // const avgRating = empTips.length
            //     ? (empTips.reduce((sum, t) => sum + (t.rating || 0), 0) / empTips.length).toFixed(1)
            //     : "N/A";
            const avgRating = empTips.length
                ? parseFloat(
                    (empTips.reduce((sum, t) => sum + Number(t.rating || 0), 0) / empTips.length).toFixed(1)
                )
                : "N/A";



            return {
                id: emp.id,
                displayName: emp.displayName,
                rawAvg: dailyAvg,
                avgPerDay: dailyAvg.toFixed(2),
                avgRating,
            };
        })
        .sort((a, b) => b.rawAvg - a.rawAvg); // 🔥 sorted by raw avg descending


    const bestRatings = [...filteredTips]
        .filter((t) => t.rating >= 4)
        .sort((a, b) => b.rating - a.rating)
        .slice(0, 5)
        .map((t) => ({
            id: t.id,
            rating: t.rating,
            amount: t.amount,
            feedback: t.feedback,
            employeeID: t.employeeID,
            date: new Date(t.createdAt.seconds * 1000).toLocaleDateString(),
        }));

    const worstRatings = [...filteredTips]
        .filter((t) => t.rating <= 2)
        .sort((a, b) => a.rating - b.rating)
        .slice(0, 5)
        .map((t) => ({
            id: t.id,
            rating: t.rating,
            amount: t.amount,
            feedback: t.feedback,
            employeeID: t.employeeID,
            date: new Date(t.createdAt.seconds * 1000).toLocaleDateString(),
        }));

    const getEmployeeNameById = (id) => {
        const emp = employees.find((e) => e.id === id);
        return emp?.displayName || "Unknown";
    };

    const employeeColorMap = employees.reduce((acc, emp, i) => {
        const goldenRatioConjugate = 0.618033988749895;
        const hue = (i * goldenRatioConjugate * 360) % 360;
        acc[emp.displayName] = `hsl(${hue}, 65%, 55%)`;
        return acc;
    }, {});

    return (
        <BusinessLayoutV2>
            <style>{`
                @import url('https://fonts.googleapis.com/css2?family=DM+Sans:ital,opsz,wght@0,9..40,300;0,9..40,400;0,9..40,500;0,9..40,600;0,9..40,700&family=JetBrains+Mono:wght@400;500&display=swap');
                .business-stats-v2 {
                    --surface: #ffffff;
                    --surface-raised: #f0f0f5;
                    --border: #e8e8ee;
                    --text-secondary: #6b6b82;
                    --accent: #0d7c5f;
                    --font: 'DM Sans', -apple-system, sans-serif;
                    --mono: 'JetBrains Mono', monospace;
                    font-family: var(--font);
                    width: 100%;
                    padding: 32px 0 80px;
                }
                .business-stats-v2 .title {
                    font-size: 28px;
                    font-weight: 700;
                    letter-spacing: -0.5px;
                    margin-bottom: 24px;
                }
                .business-stats-v2 .surface {
                    background: var(--surface);
                    border: 1px solid var(--border);
                    border-radius: 18px;
                    padding: 24px;
                }
                .business-stats-v2 .surface-soft {
                    background: var(--surface-raised);
                    border: 1px solid var(--border);
                    border-radius: 14px;
                    padding: 18px;
                }
                .business-stats-v2 .card-head {
                    font-size: 12px;
                    color: var(--text-secondary);
                    text-transform: uppercase;
                    letter-spacing: 0.7px;
                    margin-bottom: 8px;
                }
                .business-stats-v2 .card-value {
                    font-family: var(--mono);
                    font-size: 22px;
                    font-weight: 700;
                    color: var(--accent);
                }
            `}</style>
            <div className="business-stats-v2">
                <div className="w-full">
                <h1 className="title">Analytics & Statistics</h1>
                <div className="mb-6">
                    <select
                        className="border p-2 rounded-md w-full bg-white"
                        onChange={(e) => setFilterDays(FILTER_OPTIONS[e.target.value])}
                        value={Object.keys(FILTER_OPTIONS).find(label => FILTER_OPTIONS[label] === filterDays)}
                    >
                        {Object.keys(FILTER_OPTIONS).map((label) => (
                            <option key={label}>{label}</option>
                        ))}
                    </select>
                </div>
                {/* Remaining components go here (stat cards, charts, tables, etc.) */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
                    <div className="surface-soft">
                        <h2 className="card-head">Avg Tips Per Day</h2>
                        <p className="card-value">€{avgTipsPerDay}</p>
                    </div>
                    <div className="surface-soft">
                        <h2 className="card-head">Top Server</h2>
                        <p className="card-value">{topEmployee.displayName} — €{topEmployee.avg.toFixed(2)}/day</p>
                    </div>
                    <div className="surface-soft">
                        <h2 className="card-head">Tips This Period</h2>
                        <p className="card-value">€{monthlyTotal}</p>
                    </div>
                    <div className="surface-soft">
                        <h2 className="card-head">Total Tips</h2>
                        <p className="card-value">€{totalTips}</p>
                    </div>
                </div>

                {/* Employee Stats Table */}
                <section className="mb-6">
                    <h2 className="text-xl font-semibold mb-4">Employee Stats</h2>
                    <div className="overflow-x-auto rounded-2xl border border-[#e5e7eb] shadow-sm bg-white">
                        <table className="min-w-full divide-y divide-gray-200 text-sm">
                            <thead className="bg-gray-50">
                            <tr>
                                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                                    Employee
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                                    Avg Tips/Day
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                                    Avg Rating
                                </th>
                            </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-100">
                            {employeeDetailStats.map((emp) => (
                                <tr key={emp.id} className="hover:bg-gray-50 transition">
                                    <td className="px-6 py-4 font-medium text-gray-900">{emp.displayName}</td>
                                    <td className="px-6 py-4 text-gray-700">€{emp.avgPerDay}</td>
                                    <td className="px-6 py-4 text-yellow-600 font-medium">⭐ {emp.avgRating}</td>
                                </tr>
                            ))}
                            </tbody>
                        </table>
                    </div>
                </section>


                {/* Tips Trend Chart */}
                <h2 className="text-xl font-semibold mb-2">Tips Trend</h2>

                <div className="surface mb-8">
                    <ResponsiveContainer width="100%" height={240}>
                        <AreaChart data={tipChartData} margin={{ top: 4, right: 8, left: 8, bottom: 0 }}>
                            <defs>
                                <linearGradient id="statsTipGrad" x1="0" y1="0" x2="0" y2="1">
                                    <stop offset="0%" stopColor="#0d7c5f" stopOpacity={0.15} />
                                    <stop offset="100%" stopColor="#0d7c5f" stopOpacity={0} />
                                </linearGradient>
                            </defs>
                            <CartesianGrid stroke="#eeeef2" vertical={false} />
                            <XAxis
                                dataKey="date"
                                tick={{ fontSize: 11, fill: "#9b9bb0" }}
                                axisLine={false}
                                tickLine={false}
                            />
                            <YAxis
                                domain={["dataMin", "dataMax"]}
                                tick={{ fontSize: 11, fill: "#9b9bb0" }}
                                axisLine={false}
                                tickLine={false}
                                tickFormatter={(v) => `€${v}`}
                            />
                            <Tooltip content={<CustomTooltip />} />
                            <Area
                                type="monotone"
                                dataKey="amount"
                                stroke="#0d7c5f"
                                strokeWidth={2.5}
                                fill="url(#statsTipGrad)"
                                dot={false}
                                activeDot={{ r: 5, stroke: "#0d7c5f", strokeWidth: 2, fill: "#fff" }}
                            />
                        </AreaChart>
                    </ResponsiveContainer>
                </div>


                <section className="mb-6">
                    <h2 className="text-xl font-semibold mb-2">Tips by Weekday</h2>
                    <div className="surface">
                        <ResponsiveContainer width="100%" height={300}>
                            <BarChart data={tipsByWeekday}>
                                <CartesianGrid stroke="#eeeef2" vertical={false} />
                                <XAxis
                                    dataKey="day"
                                    tick={{ fontSize: 11, fill: "#9b9bb0" }}
                                    axisLine={false}
                                    tickLine={false}
                                />
                                <YAxis
                                    tick={{ fontSize: 11, fill: "#9b9bb0" }}
                                    axisLine={false}
                                    tickLine={false}
                                    tickFormatter={(v) => `€${v}`}
                                />
                                <Tooltip content={<CustomTooltip />} />
                                <Bar
                                    dataKey="amount"
                                    fill="#0d7c5f"
                                    radius={[6, 6, 0, 0]}
                                    barSize={30}
                                />
                            </BarChart>
                        </ResponsiveContainer>
                    </div>
                </section>




                <section className="mb-8">
                    {/*<h2 className="text-md font-semibold text-gray-800 mb-4">Employee Tip Trends</h2>*/}
                    <h2 className="text-xl font-semibold mb-2">Employee Tip Trends</h2>

                    <div className="surface">

                        <ResponsiveContainer width="100%" height={300}>
                            <LineChart data={employeeTrends}>
                                <CartesianGrid stroke="#eeeef2" vertical={false} />
                                <XAxis
                                    dataKey="date"
                                    tick={{ fontSize: 11, fill: "#9b9bb0" }}
                                    axisLine={false}
                                    tickLine={false}
                                />
                                <YAxis
                                    tick={{ fontSize: 11, fill: "#9b9bb0" }}
                                    axisLine={false}
                                    tickLine={false}
                                    tickFormatter={(v) => `€${v}`}
                                />
                                <Tooltip
                                    contentStyle={{
                                        borderRadius: 12,
                                        borderColor: "#e8e8ee",
                                        fontSize: "13px",
                                    }}
                                    labelStyle={{ color: "#8b8b9e" }}
                                    cursor={{ stroke: "#d1d5db", strokeWidth: 1 }}
                                />
                                <Legend
                                    wrapperStyle={{
                                        fontSize: "13px",
                                        color: "#6b7280",
                                        paddingTop: "8px",
                                    }}
                                />
                                {employees.map((emp) => (
                                    <Line
                                        key={emp.id}
                                        type="monotone"
                                        dataKey={emp.displayName}
                                        stroke={employeeColorMap[emp.displayName]}
                                        strokeWidth={3}
                                        dot={{
                                            r: 3,
                                            stroke: employeeColorMap[emp.displayName],
                                            strokeWidth: 2,
                                            fill: "white",
                                        }}
                                        activeDot={{ r: 6 }}
                                    />
                                ))}
                            </LineChart>
                        </ResponsiveContainer>
                    </div>
                </section>

                <section className="mb-6">
                    <div className="flex justify-between">
                        <h2 className="text-xl font-semibold mb-2">Best Ratings</h2>
                        {/*<div onClick={() => {}} className="hover:cursor-pointer font-semibold mb-2 underline">See all</div>*/}

                    </div>
                    <ul className="surface-soft divide-y">
                        {bestRatings.map((r) => (
                            <li key={r.id} className="py-2">
                                <p><strong>⭐ {r.rating}</strong> — {getEmployeeNameById(r.employeeID)}</p>
                                <p className="text-sm text-gray-600">🕒 {r.date} — 💰 €{r.amount.toFixed(2)}</p>
                                <p className="text-sm italic text-gray-700">{r.feedback || "No feedback"}</p>
                            </li>
                        ))}
                    </ul>
                </section>

                <section className="mb-6">
                    <div className="flex justify-between">
                        <h2 className="text-xl font-semibold mb-2">Worst Ratings</h2>
                        {/*<div onClick={() => {}} className="hover:cursor-pointer font-semibold mb-2 underline">See all</div>*/}

                    </div>
                    <ul className="surface-soft divide-y">
                        {worstRatings.length <= 0 ? <p className="text-sm text-gray-600">No bad ratings found.</p>:
                        worstRatings.map((r) => (
                            <li key={r.id} className="py-2">
                                <p><strong>⭐ {r.rating}</strong> — {getEmployeeNameById(r.employeeID)}</p>
                                <p className="text-sm text-gray-600">🕒 {r.date} — 💰 €{r.amount.toFixed(2)}</p>
                                <p className="text-sm italic text-gray-700">{r.feedback || "No feedback"}</p>
                            </li>
                        ))}
                    </ul>
                </section>
                </div>
            </div>

        </BusinessLayoutV2>
    );
}

export default BusinessStatisticsV2;
